import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { useTranslation } from '../lib/i18n';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Loader2, KeyRound, CheckCircle, AlertCircle, Eye, EyeOff } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ResetPasswordLink = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  
  const [formData, setFormData] = useState({
    new_password: '',
    new_password_confirm: ''
  });

  useEffect(() => {
    if (!token) {
      setError(t('auth.invalidResetLink') || 'Geçersiz şifre sıfırlama linki');
    }
  }, [token, t]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.new_password.length < 6) {
      toast.error(t('auth.passwordMinLength') || 'Şifre en az 6 karakter olmalıdır');
      return;
    }
    
    if (formData.new_password !== formData.new_password_confirm) {
      toast.error(t('auth.passwordsDoNotMatch') || 'Şifreler eşleşmiyor');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${API_URL}/api/auth/reset-password-link`, {
        token,
        new_password: formData.new_password,
        new_password_confirm: formData.new_password_confirm
      });

      if (response.data.success) {
        setSuccess(true);
        toast.success(t('auth.passwordResetSuccess') );
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || t('common.error');
      setError(errorMessage);
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Success state
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="h-8 w-8 text-emerald-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
              {t('auth.passwordChanged') || 'Şifre Değiştirildi'}
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mb-6">
              {t('auth.passwordResetSuccessMessage') || 'Şifreniz başarıyla değiştirildi. Artık yeni şifrenizle giriş yapabilirsiniz.'}
            </p>
            <Button asChild className="w-full">
              <Link to="/login">{t('common.login') || 'Giriş Yap'}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Error state (no token)
  if (error && !token) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-6 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="h-8 w-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-2">
              {t('auth.invalidLink') || 'Geçersiz Link'}
            </h2>
            <p className="text-slate-500 dark:text-slate-400 mb-6">{error}</p>
            <Button asChild variant="outline" className="w-full">
              <Link to="/login">{t('common.login') || 'Giriş Sayfasına Dön'}</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <KeyRound className="h-8 w-8 text-blue-600" />
          </div>
          <CardTitle>{t('auth.setNewPassword') || 'Yeni Şifre Belirle'}</CardTitle>
          <CardDescription>
            {t('auth.setNewPasswordDescription') || 'Hesabınız için yeni bir şifre belirleyin'}
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="new_password">{t('auth.newPassword') }</Label>
              <div className="relative">
                <Input
                  id="new_password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.new_password}
                  onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:text-slate-300"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="new_password_confirm">{t('auth.confirmNewPassword') || 'Yeni Şifre (Tekrar)'}</Label>
              <div className="relative">
                <Input
                  id="new_password_confirm"
                  type={showConfirmPassword ? 'text' : 'password'}
                  value={formData.new_password_confirm}
                  onChange={(e) => setFormData({ ...formData, new_password_confirm: e.target.value })}
                  placeholder="••••••••"
                  required
                  minLength={6}
                  disabled={loading}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:text-slate-300"
                >
                  {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            
            <Button type="submit" className="w-full" disabled={loading}>
              {loading ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t('common.saving') || 'Kaydediliyor...'}
                </>
              ) : (
                t('auth.setPassword') || 'Şifreyi Belirle'
              )}
            </Button>
          </form>
          
          <div className="mt-4 text-center">
            <Link to="/login" className="text-sm text-blue-600 hover:underline">
              {t('auth.backToLogin') || 'Giriş sayfasına dön'}
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ResetPasswordLink;
